/* The Highest Mountain lander: buy buttons, the preorder/shipped phase, the 3D model, the phone sticky bar and the
   scroll reveals.
   Design: docs/handoffs/the-highest-mountain-lander/README.md. Tested by storefront_theme/_review/test-thm.js on the
   fixtures rendered by _review/make-highest-mountain-fixture.cjs.

   A small focused script rather than the Knickerbockers commerce script (lander-knickerbockers-v2.js): this book is
   Pediment-only (no ownership swapper) and carries no DealEasy or Selleasy modules, so none of that applies.

   Contract with templates/product.lander-the-highest-mountain.liquid:
   - #thm-lander[data-phase="preorder"|"shipped"][data-ship-date="YYYY-MM-DD"] is the root; #thm-config holds
     {"variantId"}.
   - [data-thm-buy] groups hold a [data-thm-add] button and a [data-thm-error] line. Every button adds ONE copy of
     the config variant with a JS cart add (never a <form>: DealEasy rewrites product-form quantities store-wide).
     Sold out renders a disabled button with no [data-thm-add].
   - [data-thm-model] holds the mockup poster and, when the product has a 3D model, a <template data-thm-model-tpl>
     with Shopify's model_viewer_tag output. Near the viewport the script loads model-viewer through
     Shopify.loadFeatures (the store's own loader, no extra library), inserts the viewer over the poster and frames it
     at eye level; the poster hides once the model has loaded. Without the loader, the template or a model, the
     poster simply stays.
   - .thm-sticky is shown (.is-on) once the hero's buy group has scrolled up out of view, and hidden while
     [data-thm-closing] is on screen. CSS shows it below 880px only. While it is up on a phone, the store's bottom
     pill (#pediment-chat .pc-bar; em-popup-drawer .tab/.snack) is lifted just above it (see liftHosts).
   Everything here only enhances: no content is hidden unless this script runs. */
(function () {
  'use strict';
  var root = document.getElementById('thm-lander');
  if (!root) return;

  var cfg = {};
  try { cfg = JSON.parse((document.getElementById('thm-config') || {}).textContent || '{}'); } catch (e) {}

  var api = window.thmLander = window.thmLander || {};
  if (!api.go) api.go = function (url) { window.location.href = url; };
  var reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /* Full width without the scrollbar, so the full-bleed lander centres its rail exactly where the header centres its
     own (100vw includes a desktop scrollbar; the header's container does not). */
  function sizeToPage() { root.style.setProperty('--thm-vw', document.documentElement.clientWidth + 'px'); }
  sizeToPage();
  window.addEventListener('resize', sizeToPage);

  /* Phase: corrects a stale server-cached render. Flips at midnight UTC on the ship date, the same instant the
     Liquid check uses. */
  (function () {
    var raw = root.getAttribute('data-ship-date');
    if (!raw) return;
    var ship = new Date(raw + 'T00:00:00Z');
    if (!isNaN(ship)) root.setAttribute('data-phase', Date.now() >= ship.getTime() ? 'shipped' : 'preorder');
  })();

  /* Buy buttons: one copy, then the cart. */
  api.addToCart = function (variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity })
    }).then(function (r) {
      if (!r.ok) throw new Error('cart add failed: ' + r.status);
      api.go('/cart');
    });
  };

  function release(btn) {
    if (btn.hasAttribute('data-thm-label')) {
      btn.innerHTML = btn.getAttribute('data-thm-label');
      btn.removeAttribute('data-thm-label');
    }
    btn.disabled = false;
  }

  root.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-thm-add]');
    if (!btn || btn.disabled) return;
    var group = btn.closest('[data-thm-buy]');
    var error = group && group.querySelector('[data-thm-error]');
    var variantId = parseInt(cfg.variantId, 10);
    btn.setAttribute('data-thm-label', btn.innerHTML);
    btn.disabled = true;
    btn.textContent = 'Adding…';
    if (error) error.hidden = true;
    api.addToCart(variantId, 1).catch(function () {
      release(btn);
      if (error) error.hidden = false;
    });
  });

  /* Back from the cart restores this page from the back-forward cache with the pressed button still disabled. */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) root.querySelectorAll('[data-thm-label]').forEach(release);
  });

  /* 3D model: lazy. Same starting camera as the theme's pd-3d-camera.js (eye level, 24-degree field of view, the
     book filling about 80% of the box whichever way it turns); marking data-pd-camera first means that script, where
     a theme includes it, leaves this viewer to us. Never moves the camera once the shopper has. */
  (function () {
    var box = root.querySelector('[data-thm-model]');
    var tpl = box && box.querySelector('template[data-thm-model-tpl]');
    if (!box || !tpl) return;
    var POLAR = 85, FOV = 24, FILL = 0.8;

    function frame(mv) {
      if (mv.dataset.pdTouched || !mv.getDimensions) return;
      var r = mv.getBoundingClientRect();
      if (r.width < 50 || r.height < 50) return;
      var d = mv.getDimensions();
      if (!(d && d.x > 0 && d.y > 0)) return;
      var across = Math.sqrt(d.x * d.x + d.z * d.z);
      var t = Math.tan((FOV * Math.PI) / 360);
      var dist = Math.max(d.y / FILL / (2 * t), across / FILL / (2 * t * (r.width / r.height)));
      mv.setAttribute('field-of-view', FOV + 'deg');
      mv.setAttribute('camera-orbit', '0deg ' + POLAR + 'deg ' + dist.toFixed(3) + 'm');
      if (mv.jumpCameraToGoal) mv.jumpCameraToGoal();
    }

    function mount() {
      var frag = tpl.content.cloneNode(true);
      var mv = frag.querySelector('model-viewer');
      if (!mv) return;
      var poster = box.querySelector('.thm-model-poster');
      mv.dataset.pdCamera = '1';
      mv.setAttribute('camera-controls', '');
      mv.setAttribute('touch-action', 'pan-y');          /* a vertical swipe still scrolls the page on phones */
      mv.setAttribute('disable-zoom', '');                /* the wheel scrolls the page, never zooms the book */
      mv.setAttribute('shadow-intensity', '1');
      mv.setAttribute('interaction-prompt', 'auto');
      mv.setAttribute('camera-orbit', '0deg ' + POLAR + 'deg auto');
      mv.setAttribute('field-of-view', FOV + 'deg');
      if (poster) mv.setAttribute('poster', poster.currentSrc || poster.src);
      if (!mv.getAttribute('alt')) mv.setAttribute('alt', 'The Highest Mountain softcover in 3D');
      mv.classList.add('thm-model-viewer');
      mv.addEventListener('load', function () { frame(mv); box.classList.add('is-loaded'); });
      mv.addEventListener('camera-change', function (e) {
        if (e.detail && e.detail.source === 'user-interaction') mv.dataset.pdTouched = '1';
      });
      box.appendChild(frag);
      if (window.ResizeObserver) new ResizeObserver(function () { frame(mv); }).observe(mv);
      if (mv.loaded) { frame(mv); box.classList.add('is-loaded'); }
      api.modelMounted = true;
    }

    function start() {
      if (box.dataset.thmStarted) return;
      box.dataset.thmStarted = '1';
      if (window.customElements && customElements.get('model-viewer')) { mount(); return; }
      if (!window.Shopify || typeof Shopify.loadFeatures !== 'function') return;   /* the poster stays */
      /* Shopify's own model-viewer build, at the version model_viewer_tag declares (data-shopify-feature, 1.12 today).
         Its element registers a moment after onLoad, so wait for the definition. */
      var tagEl = tpl.content.querySelector('model-viewer');
      var version = (tagEl && tagEl.getAttribute('data-shopify-feature')) || '1.12';
      try {
        Shopify.loadFeatures([{ name: 'model-viewer', version: version, onLoad: function (err) {
          if (err) return;
          customElements.whenDefined('model-viewer').then(mount);
        } }]);
      } catch (e) {}
    }

    if (!('IntersectionObserver' in window)) { start(); return; }
    var io = new IntersectionObserver(function (entries) {
      if (entries.some(function (en) { return en.isIntersecting; })) { io.disconnect(); start(); }
    }, { rootMargin: '600px 0px' });
    io.observe(box);
  })();

  /* Sticky bar (phones): on once the hero's buy group has scrolled up out of view, off over the closing band. */
  (function () {
    var bar = root.querySelector('.thm-sticky');
    var hero = root.querySelector('.thm-hero [data-thm-buy]');
    var closing = root.querySelector('[data-thm-closing]');
    if (!bar || !hero || !('IntersectionObserver' in window)) return;
    var heroPassed = false, closingIn = false;
    var phone = window.matchMedia ? window.matchMedia('(max-width: 879.98px)') : { matches: true };

    /* The store's bottom pill (Patch chat's .pc-bar, which carries the Pediment Insiders teaser; Courier's own
       .tab or .snack where the chat is absent) is fixed 12px above the bottom of a phone screen, over this bar's
       button. Both render in open shadow roots, so page CSS cannot reach them: while the bar is up on a phone,
       each host gets data-thm-lift and a scoped rule (added once to its shadow root) sets the pill just above the
       bar; the attribute comes off when the bar hides. Nothing about the widgets themselves changes. */
    var LIFT_CSS = ':host([data-thm-lift]) .pc-bar, :host([data-thm-lift]) .tab, :host([data-thm-lift]) .snack'
      + '{bottom:calc(var(--thm-lift, 0px) + 12px + env(safe-area-inset-bottom, 0px)) !important}';
    var lifted = false;
    function liftHosts() {
      var hosts = [document.getElementById('pediment-chat')].concat([].slice.call(document.querySelectorAll('em-popup-drawer')));
      hosts.forEach(function (host) {
        if (!host || !host.shadowRoot) return;
        if (!host.shadowRoot.querySelector('style[data-thm-lift]')) {
          var st = document.createElement('style');
          st.setAttribute('data-thm-lift', '');
          st.textContent = LIFT_CSS;
          host.shadowRoot.appendChild(st);
        }
        if (lifted) {
          host.style.setProperty('--thm-lift', bar.offsetHeight + 'px');
          host.setAttribute('data-thm-lift', '');
        } else {
          host.removeAttribute('data-thm-lift');
        }
      });
    }
    /* the widgets mount late (and Courier may remount): catch hosts as they are added to the body */
    if ('MutationObserver' in window) new MutationObserver(function () { if (lifted) liftHosts(); }).observe(document.body, { childList: true });

    function update() {
      var on = heroPassed && !closingIn;
      bar.classList.toggle('is-on', on);
      var lift = on && phone.matches;
      if (lift !== lifted) { lifted = lift; liftHosts(); }
    }
    if (phone.addEventListener) phone.addEventListener('change', update);
    new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { heroPassed = !en.isIntersecting && en.boundingClientRect.top < 0; });
      update();
    }).observe(hero);
    if (closing) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { closingIn = en.isIntersecting; });
        update();
      }).observe(closing);
    }
  })();

  /* Reveals: restrained editorial fade-ups, tagged here so the page is complete without this script. Never the
     hero, a price, a buy button or the sticky bar. Nothing is tagged for reduced motion. If no observer callback
     arrives within three seconds (a background tab, a hidden preview pane) everything is revealed. */
  (function () {
    if (reduce || !('IntersectionObserver' in window)) return;
    var groups = [
      ['.thm-premise-copy', 0], ['.thm-story-head', 0], ['.thm-station', 90], ['.thm-praise .thm-head', 0],
      ['.thm-praise-lead', 0], ['.thm-praise-item', 90], ['.thm-life-head', 0], ['.thm-plate', 90],
      ['.thm-author-id', 0], ['.thm-bio', 0], ['.thm-also-book', 90], ['.thm-closing-title', 0], ['.thm-closing-sub', 0]
    ];
    var NEVER = '.thm-hero, .thm-sticky';
    var HOLDS = '.thm-price, [data-thm-buy], .thm-btn';
    var tagged = [];

    groups.forEach(function (g) {
      var seen = [];
      root.querySelectorAll(g[0]).forEach(function (el) {
        if (el.closest(NEVER) || el.querySelector(HOLDS)) return;
        var siblings = seen.filter(function (s) { return s.parentNode === el.parentNode; }).length;
        seen.push(el);
        if (g[1] && siblings) el.style.transitionDelay = (siblings * g[1]) + 'ms';
        el.setAttribute('data-thm-reveal', '');
        tagged.push(el);
      });
    });
    if (!tagged.length) return;

    function show(el) { el.classList.add('is-in'); }
    var heard = false;
    var io = new IntersectionObserver(function (entries) {
      heard = true;
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        show(en.target);
        io.unobserve(en.target);
      });
    }, { threshold: 0.12 });
    tagged.forEach(function (el) { io.observe(el); });

    api.observerHeard = function () { return heard; };
    api.revealAll = function () {
      tagged.forEach(show);
      io.disconnect();
    };
    setTimeout(function () { if (!heard) api.revealAll(); }, 3000);
  })();
})();
